import {
  sliceItemContentSchema,
  useSliceSelection,
} from "@prismicio/editor-fields"
import { insertAtIndex } from "@prismicio/editor-support/Array"
import { localStorage } from "@prismicio/editor-support/DOM"
import { delay } from "@prismicio/editor-support/Promise"
import { pluralize } from "@prismicio/editor-support/String"
import { useToast } from "@prismicio/editor-ui"
import type {
  Document,
  SliceItemContent,
} from "@prismicio/types-internal/lib/content"
import { useSyncExternalStore } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { z } from "zod"

import { sliceTableCopiedSlice } from "../../../app/constants"
import { trackCopyPasteSlice } from "../../../app/services/tracking"
import { useDocument, useDocumentReadOnly } from "../../DocumentProvider"
import { rememberPastedSliceHeight } from "./slicePreview/SlicePreview"
import {
  type SectionSliceConfig,
  updateSliceZone,
  type VisualSlice,
} from "./sliceTableData"
import { createSlice } from "./utils/createSlice"
import { useGetSliceTrackingInfo } from "./utils/useGetSliceTrackingInfo"

interface UseSliceTableCopyPasteArgs {
  content: Document
  slices: VisualSlice[]
  sliceConfigs: SectionSliceConfig[] | undefined
  edit: (newContent: Document) => void
  setEnableAnimations: (enabled: boolean) => void
}

export function useSliceTableCopyPaste(args: UseSliceTableCopyPasteArgs) {
  const { content, slices, sliceConfigs, edit, setEnableAnimations } = args

  const { data } = useDocument()
  const getSliceTrackingInfo = useGetSliceTrackingInfo()
  const canPaste = useCanPaste()
  const { showToast } = useToast()
  const selection = useSliceSelection()

  // Only can copy from the AnimatedList's zone.
  const copyPasteRef = useHotkeys<HTMLDivElement>("mod+c", () =>
    onCopy(selection.ids, true),
  )

  const readOnly = useDocumentReadOnly()

  // We allow pasting from anywhere, with some restrictions.
  useHotkeys("mod+v", () => onPaste(selection.ids.at(-1), true), {
    enabled: !readOnly,
  })

  function onCopy(keys: string[], fromKeyboard: boolean = false) {
    const copiedSlices = keys.flatMap(
      (key) => slices.find((slice) => slice.key === key) ?? [],
    )

    const firstCopiedSlice = copiedSlices[0]
    if (!firstCopiedSlice) return

    const copy: SerializedCopy = copiedSlices.map((copiedSlice) =>
      createSlice({
        uuid: copiedSlice.uuid,
        sliceId: copiedSlice.name,
        widget: copiedSlice.content,
      }),
    )

    /**
     * LocalStorage is better overall compared to navigator.clipboard:
     * 1) It won't ask for permissions. If the user misclicks or misjudges, the feature is gone for pretty much forever.
     * 2) We don't want the copy paste to be performed across repos: the slices may share their name but be wildly incompatible.
     * 3) We don't need/want the copy paste to be compatible with other apps.
     */
    localStorage.write(sliceTableCopiedSlice, JSON.stringify(copy))

    // Quick, feel-good toast.
    const count = copiedSlices.length
    showToast({
      title: `${count} ${pluralize(count, "Slice")} copied!`,
      seconds: 2,
    })

    trackCopyPasteSlice("copy", {
      ...getSliceTrackingInfo(firstCopiedSlice),
      keyboard: fromKeyboard,
    })
  }

  async function onPaste(
    sliceKey: string | undefined,
    fromKeyboard: boolean = false,
  ) {
    // We're happy to take over the Global CMD + V shortcut as long as we're not currently focusing some unrelated element.
    if (
      fromKeyboard &&
      document.activeElement !== document.body &&
      !copyPasteRef.current?.contains(document.activeElement)
    )
      return

    if (!sliceConfigs) return

    if (!localStorage.has(sliceTableCopiedSlice))
      return showToast({ title: "Did you forget to copy a slice first?" })

    const copy = localStorage.readJSON(
      sliceTableCopiedSlice,
      serializedCopySchema,
    )

    const pasteLocation = getBestSlicePasteLocation(
      slices,
      sliceConfigs,
      sliceKey,
    )

    if (!pasteLocation || !copy?.length)
      return showToast({
        title: "A technical error prevents us from pasting the slices.",
      })

    const { zoneId } =
      pasteLocation.type === "afterSlice" ? pasteLocation.slice : pasteLocation

    const forbiddenSharedSlices: SerializedCopy = []

    const allowedSlices = copy.flatMap((slice) => {
      if (slice.widget.__TYPE__ !== "SharedSliceContent") return slice

      const hasConfig = sliceConfigs.some(
        (config) =>
          config.zoneId === zoneId && config.sliceReference === slice.name,
      )

      if (hasConfig) return slice

      forbiddenSharedSlices.push(slice)
      return []
    })

    if (forbiddenSharedSlices.length) {
      const names = forbiddenSharedSlices.map((slice) => slice.name).join(",")

      return showToast({
        title: `The slice zone "${zoneId}" does not allow the following slices: ${names}.`,
      })
    }

    if (!allowedSlices.length) return

    const clonedSlices = allowedSlices.map((slice) => {
      const clonedSlice = createSlice({
        sliceId: slice.name,
        widget: slice.widget,
      })

      rememberPastedSliceHeight(slice.key, clonedSlice.key, data)

      return clonedSlice
    })

    setEnableAnimations(false)
    edit(pasteSlices(content, clonedSlices, pasteLocation))

    const firstClonedSlice = clonedSlices[0]

    if (firstClonedSlice) {
      selection.update({ id: firstClonedSlice.key, requestedFrom: "all" })

      trackCopyPasteSlice("paste", {
        ...getSliceTrackingInfo(firstClonedSlice, zoneId),
        keyboard: fromKeyboard,
      })
    }
    // Re-enable animations a bit later, when the state transition already started or finished.
    await delay(200)
    setEnableAnimations(true)
  }

  return {
    copyPasteRef,
    canPaste,
    onCopy,
    onPaste,
  }
}

type PasteSliceLocation =
  | { type: "afterSlice"; slice: VisualSlice }
  | { type: "zone"; zoneId: string }

export function getBestSlicePasteLocation(
  slices: VisualSlice[],
  sliceConfigs: SectionSliceConfig[],
  selectionKey?: string,
): PasteSliceLocation | undefined {
  // Note: we don't clean up the URL when deleting the last slice
  // so right now it's possible to have a selectionKey without any slices.
  if (selectionKey && slices.length) {
    const slice = slices.find((slice) => slice.key === selectionKey)
    return slice && { type: "afterSlice", slice }
  }

  // We assume most setups have a unique Slice zone.
  const firstSliceZone = sliceConfigs[0]?.zoneId
  return firstSliceZone ? { type: "zone", zoneId: firstSliceZone } : undefined
}

export function pasteSlices(
  content: Document,
  pastedSlices: SliceItemContent[],
  location: PasteSliceLocation,
) {
  if (location.type === "afterSlice")
    return updateSliceZone(content, location.slice.zoneId, (zone) => {
      const afterSliceIndex = zone.value.findIndex(
        (s) => s.key === location.slice.key,
      )

      if (afterSliceIndex === -1) return zone

      return {
        ...zone,
        value: insertAtIndex(zone.value, afterSliceIndex + 1, ...pastedSlices),
      }
    })

  return updateSliceZone(content, location.zoneId, (zone) => {
    return {
      ...zone,
      value: insertAtIndex(zone.value, 0, ...pastedSlices),
    }
  })
}

function useCanPaste() {
  return useSyncExternalStore(localStorage.subscribe, () =>
    localStorage.has(sliceTableCopiedSlice),
  )
}

const serializedCopySchema = z.array(sliceItemContentSchema)

type SerializedCopy = z.TypeOf<typeof serializedCopySchema>
